// hooks/NotificationContext.jsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { getUnreadNotificationsCountByUser } from "./../services/notificationServices";
import { useUser } from "./../pages/Home/UserContext";

const NotificationContext = createContext({
  unreadCount: 0,
  setUnreadCount: () => {},   // compat rétro
  refresh: () => {},
});

export const NotificationProvider = ({ children, pollMs = 60000 }) => {
  const { user } = useUser();
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || !user.userId) return setUnreadCount(0);
    try {
      const count = await getUnreadNotificationsCountByUser();
      setUnreadCount(Number(count || 0));
    } catch {}
  }, [user]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (user && user.userId) {
      refresh();
      intervalRef.current = setInterval(refresh, pollMs);

      const onVisibility = () => document.visibilityState === "visible" && refresh();
      const onFocus = () => refresh();

      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("focus", onFocus);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("focus", onFocus);
      };
    } else {
      setUnreadCount(0);
      return () => intervalRef.current && clearInterval(intervalRef.current);
    }
  }, [user, pollMs, refresh]);

  return (
    <NotificationContext.Provider value={{ unreadCount, setUnreadCount, refresh }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
